import { Fragment } from "react"

import { Flex, Popover } from "@/atoms"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { TEASERS } from "@/const/teaser.constants"
import { getDateInFormat, MONTH_YEAR } from "@/helpers/date.helpers"
import { TIME_FILTERS } from "@/helpers/filters.helper"
import { useHttp } from "@/hooks/useHttp"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useTranslation } from "@/hooks/useTranslation"
import { Heading } from "@/molecules/Heading"
import {
  getInsightDefaultFilters,
  useStatisticsDashboard
} from "@/pages/DashboardPage/applications/useStatistics"
import { CountBar } from "@/pages/DashboardPage/components/molecules/CountBar"
import { EmptyWidget } from "@/pages/DashboardPage/components/molecules/EmptyWidget"
import { FilterTitleCard } from "@/pages/DashboardPage/components/molecules/FilterTitleCard"
import CardDashboardTemplate from "@/pages/DashboardPage/components/templates/CardDashboardTemplate"

import styles from "./ResolvedIssuesMTTR.module.scss"

import type { ResolvedIssuesResponse } from "@/api/useStatistics.types"

export const ResolvedIssuesMTTR = () => {
  const { t } = useTranslation()
  const { t: tWidget } = useTranslation("translation", {
    keyPrefix: "dashboard.resolvedIssuesWidget"
  })
  const bars = [
    { label: "critical", limit: 7 },
    { label: "high", limit: 14 },
    { label: "medium", limit: 30 },
    { label: "low", limit: 90 }
  ]

  const [{ response, isLoading }, state, actions] =
    useStatisticsDashboard<ResolvedIssuesResponse>({
      defaultFilter: getInsightDefaultFilters(),
      needle: "/widgets/mttr"
    })
  const { bySeverity, totalFilteredIssues, totalIssuesInScope } =
    response?.data || {}
  const countContext = useHttp<{ data: number }>(
    `${RESOURCES_NAMES.TRIAGE.ISSUES_COUNT}`
  )
  const { abbreviateNumber } = useNumberConvertor()
  const loading = isLoading || countContext.isLoading
  const issuesCount = countContext.response?.data || 0

  return (
    <CardDashboardTemplate
      loading={loading}
      title={tWidget("title")}
      subTitle={t("dashboard.discoveredSince", {
        date: state[0].since ? getDateInFormat(state[0].since, MONTH_YEAR) : ""
      })}
      contentPopover={tWidget("tooltip")}
      aside={
        <FilterTitleCard
          values={state[0]}
          onOk={state[1]}
          loading={loading}
          count={totalFilteredIssues}
          total={totalIssuesInScope}
        />
      }
      teasers={{
        dataReadiness: true,
        scmInstalled: TEASERS.SCM_INSTALLED.codeRepoNotConnected(t)
      }}
    >
      <EmptyWidget
        count={totalFilteredIssues}
        total={totalIssuesInScope}
        shouldHideScanProgressEmptyTable={issuesCount > 0}
        actions={actions}
      >
        <Flex className={styles.grid}>
          {bars.map(({ label, limit }, index) => {
            const { issueCount = 0, averageDays = 0 } =
              bySeverity?.[index] || {}
            const value = Math.round(averageDays)
            const currentSeverity = t(`vulnerabilities.${label}`)

            return (
              <Fragment key={label}>
                <Flex vertical>
                  <Heading level={6} title={currentSeverity} />
                  <span className={styles.mttr}>
                    {tWidget("mttr", { mttr: limit })}
                  </span>
                </Flex>
                <Popover
                  rootClassName={styles.popoverRoot}
                  content={
                    <Flex vertical gap={8} className={styles.popoverContent}>
                      <Heading
                        level={6}
                        title={`${t(`vulnerabilities.${label}`)} ${t("general.severity")} ${t("general.issue_other")}`}
                      />
                      <Flex justify="space-between">
                        <span className="capitalize-text-ks">
                          {t("general.issue", { count: issueCount })}
                        </span>
                        <span>{abbreviateNumber(issueCount)}</span>
                      </Flex>
                      <Flex justify="space-between">
                        <span>{tWidget("popover.your")}</span>
                        <span>{`${value} ${t("general.day", { count: value })}`}</span>
                      </Flex>
                      <Flex justify="space-between">
                        <span>{tWidget("popover.recommended")}</span>
                        <span>{`${limit} ${t("days", { count: limit })}`}</span>
                      </Flex>
                    </Flex>
                  }
                >
                  <div
                    className={styles.bar}
                    onClick={() => {
                      const filter = { severity: currentSeverity } as Record<
                        string,
                        string
                      >

                      if (state[0].since)
                        filter[TIME_FILTERS.RESOLVED] =
                          actions.getDefaultFilterRange(state[0].since)

                      actions.redirectToTriage(filter)
                    }}
                  >
                    <CountBar limit={limit} value={value} />
                  </div>
                </Popover>
              </Fragment>
            )
          })}
        </Flex>
      </EmptyWidget>
    </CardDashboardTemplate>
  )
}
